import { Loader, LoaderProps } from '@mantine/core';
import { FC } from 'react';

type LoadingVariant = 'page' | 'section' | 'inline';

interface LoadingProps extends LoaderProps {
    /**
     * Controls overall layout of the loader.
     * - page: fullscreen center, min-h-screen
     * - section: fills available width with moderate min-height
     * - inline: renders inline-flex without extra wrappers
     */
    variant?: LoadingVariant;
    /**
     * Optional helper text displayed next to/below the spinner.
     */
    label?: string;
    /**
     * Tailwind classes applied to the label.
     */
    labelClassName?: string;
    /**
     * When false, removes the preset min-height from page/section variants.
     */
    fullHeight?: boolean;
    /**
     * Additional classes applied to the root wrapper.
     */
    className?: string;
}

const variantHeightMap: Record<Exclude<LoadingVariant, 'inline'>, string> = {
    page: 'min-h-screen',
    section: 'min-h-80',
};

const Loading: FC<LoadingProps> = ({
    variant = 'section',
    label,
    labelClassName,
    fullHeight = true,
    className = '',
    ...loaderProps
}) => {
    const labelClasses = labelClassName ?? 'text-sm';
    const labelStyle = labelClassName ? undefined : { color: 'var(--app-text-muted)' };

    if (variant === 'inline') {
        return (
            <span className={`inline-flex items-center gap-2 ${className}`.trim()}>
                <Loader {...loaderProps} />
                {label ? <span className={labelClasses} style={labelStyle}>{label}</span> : null}
            </span>
        );
    }

    const heightClass = fullHeight ? variantHeightMap[variant] : '';
    return (
        <div className={`w-full flex items-center justify-center ${heightClass} ${className}`.trim()}>
            <div className="flex flex-col items-center gap-2 text-center">
                <Loader {...loaderProps} />
                {label ? <span className={labelClasses} style={labelStyle}>{label}</span> : null}
            </div>
        </div>
    );
};

export default Loading;